import React, { useContext, useState, useEffect, useImperativeHandle, useRef } from 'react';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import InputValidatorField from '../../common/components/Inputs/InputValidatorField';
import WithActions from '../../common/components/Wrappers/WithActions';
import { TemplateContext, useTemplateContext } from "../../../providers/TemplateProvider";
import UseTemplateModel from "./Hooks/UseTemplateModel";
import { AuthContext } from "../../auth/providers/AuthProvider";
import {ThemeContext} from "styled-components";
import {Typography} from "@mui/material";
import { useQueryClient } from 'react-query';
import { CsrfContext } from "../../../providers/CsrfProvider";
import { useNavigate } from 'react-router-dom';
import GroupSelector from '../../common/components/GroupSelector';
import { useGroup } from "../../../providers/GroupProvider";
import { moveBillToGroup } from "../../../services/template/Templates";

const TemplateModel = ({ id, onSaveSuccess, onArchiveSuccess, hideButtons = false, onArchiveRef, dialogPaperRef }) => {
    const { formData, setFormData, handleSubmit, isLoading, isError, mutation, hasChanges } = UseTemplateModel(id);
    const { ownerSub } = useContext(TemplateContext);
    const { authClaims } = useContext(AuthContext);
    const { csrfToken } = useContext(CsrfContext);
    const theme = useContext(ThemeContext);
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const { groups, refreshGroups, createGroup } = useGroup();
    const [currentGroupId, setCurrentGroupId] = useState(null);
    const [movingGroup, setMovingGroup] = useState(false);
    const [groupInputValue, setGroupInputValue] = useState('');
    const groupSelectorRef = useRef(null);
    const hasInitializedGroupId = useRef(false);
    const isUpdatingGroup = useRef(false); // Track when we're actively updating the group

    const handleInputChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
    };

    // Get current group_id for the bill - sync with formData.group_id
    useEffect(() => {
        // Don't update from formData if we're actively updating the group (optimistic update)
        if (isUpdatingGroup.current) {
            return;
        }
        
        // Only update when formData is loaded (not loading state)
        if (!formData || formData.name === 'loading...') {
            return;
        }
        
        // Always sync currentGroupId with formData.group_id
        // formData.group_id can be:
        // - A valid group ID (string UUID)
        // - null/undefined (bill has no group - default group)
        const groupIdFromForm = formData.group_id || null;
        
        // Only update if it's different from current value (to avoid unnecessary updates)
        if (String(currentGroupId || '') !== String(groupIdFromForm || '')) {
            setCurrentGroupId(groupIdFromForm);
            hasInitializedGroupId.current = true;
            
            // Set the input value based on the group
            if (groupIdFromForm && groups && groups.length > 0) {
                const group = groups.find(g => String(g.id) === String(groupIdFromForm));
                if (group) {
                    setGroupInputValue(group.name);
                } else {
                    // Group not found yet (might still be loading), keep current value
                }
            } else {
                // No group (default group)
                setGroupInputValue('Default Group');
            }
        }
    }, [formData, groups, currentGroupId]);

    const handleGroupChange = async (e) => {
        // Check if this is a new_group_name event (user typing new group name)
        if (e.target.name === 'new_group_name') {
            // Don't do anything here - we'll handle new group creation on save
            // Just update the input value tracking
            setGroupInputValue(e.target.value || '');
            return;
        }
        
        const newGroupId = e.target.value === '' || e.target.value === null ? null : e.target.value;
        
        // Update input value when a group is selected
        if (newGroupId) {
            const selectedGroup = groups.find(g => String(g.id) === String(newGroupId));
            if (selectedGroup) {
                setGroupInputValue(selectedGroup.name);
            }
        } else {
            // No group selected (bills without a group)
            setGroupInputValue('');
        }
        
        // Don't move if it's the same group
        if (newGroupId === currentGroupId) {
            return;
        }

        // Allow null groupId - this removes the bill from a group (moves to default group)
        // Note: Backend may need to support this - if it doesn't, we'll need to add backend support

        // Store the previous group ID in case we need to revert on error
        const previousGroupId = currentGroupId;
        
        // Set the new group ID optimistically so the UI updates immediately
        isUpdatingGroup.current = true; // Prevent useEffect from overwriting our optimistic update
        setCurrentGroupId(newGroupId);
        hasInitializedGroupId.current = true; // Mark as initialized so we don't reset it
        
        setMovingGroup(true);
        try {
            await moveBillToGroup(id, newGroupId, csrfToken);
            // Refresh groups first to ensure they're up to date
            await refreshGroups();
            // Then invalidate queries to refresh the bill list and formData
            queryClient.invalidateQueries("templates");
            queryClient.invalidateQueries(['templates', String(id)]);
            
            // Wait a bit for the query to refetch and update formData
            // Then allow useEffect to sync again
            setTimeout(() => {
                isUpdatingGroup.current = false;
            }, 500);
        } catch (error) {
            console.error('Error moving bill to group:', error);
            // Revert to previous group ID on error
            isUpdatingGroup.current = false;
            setCurrentGroupId(previousGroupId);
            alert('Error moving bill to group: ' + (error.response?.data?.error || error.message));
        } finally {
            setMovingGroup(false);
        }
    };

    // Only show loading if we're actually loading AND don't have form data yet
    if (isLoading && (!formData || formData.name === 'loading...')) {
        return <Typography sx={{color:theme.colors.text}}>Loading...</Typography>;
    }

    if (isError) {
        return <Typography sx={{color:theme.colors.text}}>Error fetching templates, try refreshing</Typography>;
    }

    const isEditable = () => {
        return authClaims.user_sub.toUpperCase() === ownerSub.toUpperCase()
    }

    const isArchived = formData?.archived_at != null && formData.archived_at !== '';

    const handleArchive = (e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        const updatedFormData = {
            ...formData,
            id: String(id),
            archived: !isArchived
        };
        mutation.mutate(updatedFormData, {
            onSuccess: () => {
                queryClient.invalidateQueries("templates");
                queryClient.invalidateQueries(['templates', String(id)]);
                if (!isArchived) {
                    // If archiving, navigate to homepage
                    navigate('/dashboard');
                } else {
                    // If unarchiving, call the success callback to close settings
                    if (onArchiveSuccess) {
                        onArchiveSuccess();
                    }
                }
            }
        });
    };

    // Expose handleArchive and handleSubmit via ref if provided
    useImperativeHandle(onArchiveRef, () => ({
        handleArchive: () => handleArchive(null),
        handleSubmit: () => {
            handleFormSubmit(null);
        },
        isArchived,
        hasChanges,
        isLoading: mutation.isLoading,
    }), [isArchived, mutation.isLoading, formData, id, onSaveSuccess, onArchiveSuccess, queryClient]);

    const handleFormSubmit = async (e) => {
        if (e) {
            e.preventDefault();
        }
        
        // Get the current input value from the GroupSelector if available
        let currentInputValue = groupInputValue;
        if (groupSelectorRef.current && groupSelectorRef.current.getInputValue) {
            const refValue = groupSelectorRef.current.getInputValue();
            if (refValue) {
                currentInputValue = refValue;
            }
        }
        
        // Also check formData for new_group_name as fallback
        if (!currentInputValue && formData && formData.new_group_name) {
            currentInputValue = formData.new_group_name;
        }
        
        // Last resort: try to get it from the DOM
        if (!currentInputValue && dialogPaperRef?.current) {
            const input = dialogPaperRef.current.querySelector('input[type="text"][placeholder*="group" i], input[type="text"][placeholder*="Group" i]');
            if (input && input.value) {
                currentInputValue = input.value;
                console.log('Got input value from DOM:', currentInputValue);
            }
        }
        
        console.log('handleFormSubmit called', { 
            formData, 
            id, 
            groupInputValue, 
            currentInputValue,
            currentGroupId,
            groups: groups?.map(g => ({ id: g.id, name: g.name }))
        });
        
        // Check if user typed a new group name that doesn't match any existing group
        let groupIdToUse = currentGroupId;
        const trimmedInput = currentInputValue ? String(currentInputValue).trim() : '';
        
        // Check if "Default Group" was selected
        if (trimmedInput.toLowerCase() === 'default group') {
            groupIdToUse = null;
            console.log('Default Group selected, removing bill from group');
            // If it's different from current, move the bill (remove from group)
            if (groupIdToUse !== currentGroupId) {
                try {
                    console.log('Removing bill from group (moving to default)');
                    await moveBillToGroup(id, null, csrfToken);
                    setCurrentGroupId(null);
                    hasInitializedGroupId.current = true;
                    await refreshGroups();
                    console.log('Successfully removed bill from group');
                } catch (error) {
                    console.error('Error removing bill from group:', error);
                    // If backend doesn't support null group_id yet, show a helpful error
                    if (error.response?.status === 400) {
                        alert('Removing bills from groups is not yet supported. Please select a group.');
                    } else {
                        alert('Error removing bill from group: ' + (error.response?.data?.error || error.message));
                    }
                    return; // Don't proceed with save if group move failed
                }
            }
        } else if (trimmedInput && trimmedInput.length > 0) {
            console.log('Processing group input:', trimmedInput);
            // Check if input matches an existing group (case-insensitive)
            const matchingGroup = groups && groups.length > 0 ? groups.find(g => {
                const groupName = (g.name || '').toLowerCase().trim();
                return groupName === trimmedInput.toLowerCase();
            }) : null;
            
            console.log('Matching group found:', matchingGroup);
            
            if (matchingGroup) {
                // Use the matching group
                groupIdToUse = matchingGroup.id;
                console.log('Using existing group:', groupIdToUse);
                // If it's different from current, move the bill
                if (groupIdToUse !== currentGroupId) {
                    try {
                        console.log('Moving bill to existing group:', groupIdToUse);
                        await moveBillToGroup(id, groupIdToUse, csrfToken);
                        setCurrentGroupId(groupIdToUse);
                        hasInitializedGroupId.current = true;
                        await refreshGroups();
                        console.log('Successfully moved bill to group');
                    } catch (error) {
                        console.error('Error moving bill to group:', error);
                        alert('Error moving bill to group: ' + (error.response?.data?.error || error.message));
                        return; // Don't proceed with save if group move failed
                    }
                }
            } else {
                // Create a new group with the typed name
                console.log('Creating new group with name:', trimmedInput);
                try {
                    setMovingGroup(true);
                    const newGroup = await createGroup(trimmedInput);
                    console.log('New group created:', newGroup);
                    if (newGroup && newGroup.id) {
                        groupIdToUse = newGroup.id;
                        console.log('Moving bill to new group:', groupIdToUse);
                        // Move bill to the new group
                        await moveBillToGroup(id, groupIdToUse, csrfToken);
                        setCurrentGroupId(groupIdToUse);
                        hasInitializedGroupId.current = true;
                        await refreshGroups();
                        console.log('Successfully created group and moved bill');
                    } else {
                        throw new Error('Failed to create group: no ID returned');
                    }
                } catch (error) {
                    console.error('Error creating group:', error);
                    alert('Error creating group: ' + (error.response?.data?.error || error.message));
                    setMovingGroup(false);
                    return; // Don't proceed with save if group creation failed
                } finally {
                    setMovingGroup(false);
                }
            }
        } else {
            console.log('No group input value to process');
        }
        
        const submitData = {
            ...formData,
            id: String(id),
        };
        console.log('Submitting data:', submitData);
        mutation.mutate(submitData, {
            onSuccess: (data) => {
                console.log('Template saved successfully:', data);
                // Invalidate queries to refresh data
                queryClient.invalidateQueries(['templates', String(id)]);
                queryClient.invalidateQueries("templates");
                if (onSaveSuccess) {
                    onSaveSuccess();
                }
            },
            onError: (error) => {
                console.error('Error saving template:', error);
                alert('Error saving template: ' + (error.response?.data?.error || error.message));
            }
        });
    };

    return (
        <Box component="form" onSubmit={handleFormSubmit} sx={{ width: '100%' }}>
            <WithActions inputType="text" forceSpacing>
                <InputValidatorField
                    name="name"
                    label="Name"
                    type="text"
                    value={formData.name || ''}
                    handleChange={handleInputChange}
                    disabled={!isEditable()}
                    InputLabelProps={{
                        style: { color: theme.colors.text },
                    }}
                    InputProps={{
                        style: { color: theme.colors.text },
                    }}
                    sx={{ mb: 2 }}
                />
            </WithActions>
            <WithActions inputType="text" forceSpacing>
                <InputValidatorField
                    name="description"
                    label="Description"
                    type="text"
                    value={formData.description || ''}
                    handleChange={handleInputChange}
                    disabled={!isEditable()}
                    InputLabelProps={{
                        style: { color: theme.colors.text },
                    }}
                    InputProps={{
                        style: { color: theme.colors.text },
                    }}
                    sx={{ mb: 2 }}
                />
            </WithActions>
            <WithActions inputType="text" forceSpacing>
                <InputValidatorField
                    name="typical_cost"
                    label="Typical Cost"
                    type="number"
                    value={formData.typical_cost || ''}
                    handleChange={handleInputChange}
                    disabled={!isEditable()}
                    InputLabelProps={{
                        style: { color: theme.colors.text },
                    }}
                    InputProps={{
                        style: { color: theme.colors.text },
                    }}
                    sx={{ mb: 2 }}
                />
            </WithActions>
            {isEditable() && (
                <WithActions inputType="text" forceSpacing>
                    <GroupSelector
                        ref={groupSelectorRef}
                        value={currentGroupId}
                        onChange={handleGroupChange}
                        onInputValueChange={setGroupInputValue}
                        disabled={movingGroup || isLoading}
                        showCreateOption={false}
                        filterOwnedOnly={true}
                        menuContainer={dialogPaperRef?.current || null}
                        skipWrapper={true}
                        sx={{ mb: 2 }}
                    />
                </WithActions>
            )}
            {!hideButtons && isEditable() && (
                <>
                    <Button fullWidth type="submit" variant="contained" color="primary" disabled={isLoading} sx={{ marginBottom: 1 }}>
                        {mutation.isLoading ? "Saving..." : "Save"}
                    </Button>
                    <Button 
                        fullWidth 
                        type="button"
                        variant="outlined" 
                        color={isArchived ? "success" : "warning"} 
                        disabled={mutation.isLoading}
                        onClick={handleArchive}
                    >
                        {mutation.isLoading 
                            ? (isArchived ? "Unarchiving..." : "Archiving...") 
                            : (isArchived ? "Unarchive" : "Archive")}
                    </Button>
                </>
            )}
            {isError && <p>Error: Unable to update the template</p>}
        </Box>
    );
};

export default TemplateModel;
